import { api, downloadFile } from '@/api/api-client';
import type {
  CustomerBillingSummary,
  CustomerNotification,
  ContractingCatalog,
  ContractingPreview,
  ContractingResult,
  ContractingSelection,
  FiscalProfileInput,
  PlanUpgradeRequest,
} from '@/types/customer-billing';
import type { CustomerContractListItem } from '@/types/master-platform';

export const customerBillingQueryKey = ['customer-billing', 'summary'] as const;

export const customerBillingService = {
  summary: async () => (await api.get<CustomerBillingSummary>('/customer-billing/summary')).data,
  confirmInfinitePayReturn: async (input: {
    order_nsu: string;
    slug: string;
    transaction_nsu: string;
    capture_method: string;
    receipt_url?: string;
  }) => (await api.post('/customer-billing/infinitepay/confirm-return', input)).data,
  updateFiscalProfile: async (input: FiscalProfileInput) =>
    (await api.put('/customer-billing/fiscal-profile', input)).data,
  updatePaymentMethod: async (paymentMethod: 'PIX' | 'BOLETO' | 'CREDIT_CARD' | 'PIX_AUTOMATIC') =>
    (
      await api.put<CustomerBillingSummary>('/customer-billing/payment-method', {
        paymentMethod,
      })
    ).data,
  requestUpgrade: async (input: { planId: string; notes?: string | null }) =>
    (await api.post<PlanUpgradeRequest>('/customer-billing/upgrade-requests', input)).data,
  requestPlanChange: async (planId: string) =>
    (
      await api.post<CustomerBillingSummary>('/customer-billing/plan-change', {
        planId,
      })
    ).data,
  upgradeRequests: async () =>
    (await api.get<PlanUpgradeRequest[]>('/customer-billing/upgrade-requests')).data,
  notifications: async () =>
    (await api.get<CustomerNotification[]>('/customer-billing/notifications')).data,
  markNotificationRead: async (id: string) =>
    (await api.patch<CustomerNotification>(`/customer-billing/notifications/${id}/read`, {})).data,
  activateSubscription: async () =>
    (await api.post<{ checkoutUrl?: string | null }>('/customer-billing/subscription/activate'))
      .data,
  requestCancellation: async (reason: string) =>
    (
      await api.post<CustomerBillingSummary>('/customer-billing/subscription/cancel', {
        confirmation: true,
        reason,
      })
    ).data,
  contractingCatalog: async () =>
    (await api.get<ContractingCatalog>('/customer-billing/contracting/catalog')).data,
  contractingPreview: async (input: ContractingSelection) =>
    (await api.post<ContractingPreview>('/customer-billing/contracting/preview', input)).data,
  contractingConfirm: async (
    input: ContractingSelection & {
      termsHash: string;
      idempotencyKey: string;
      acceptedCommercialTerms: true;
    },
  ) => (await api.post<ContractingResult>('/customer-billing/contracting/confirm', input)).data,
  contractingResume: async () =>
    (await api.post<ContractingResult>('/customer-billing/contracting/resume')).data,
  secondCopy: async (invoiceId: string) =>
    (await api.post<CustomerBillingSummary>(`/customer-billing/invoices/${invoiceId}/second-copy`))
      .data,
  generatePix: async (invoiceId: string) =>
    (await api.post<CustomerBillingSummary>(`/customer-billing/invoices/${invoiceId}/pix`)).data,
  resend: async (invoiceId: string) =>
    (await api.post<CustomerBillingSummary>(`/customer-billing/invoices/${invoiceId}/resend`)).data,
  syncPayment: async (invoiceId: string) =>
    (await api.post<CustomerBillingSummary>(`/customer-billing/invoices/${invoiceId}/sync`)).data,
  receipt: (invoiceId: string) => downloadFile(`/customer-billing/invoices/${invoiceId}/receipt`),
  contracts: async () =>
    (await api.get<CustomerContractListItem[]>('/customer-billing/contracts')).data,
  contractDownload: (id: string) => downloadFile(`/customer-billing/contracts/${id}/download`),
};
